import NoFlyZone from './NoFlyZone.js';
import Obstacle from './Obstacle.js';
import Annotation from './Annotation.js';
import LandUse from './LandUse.js';
import { init3DTilesCache } from '@/utils/indexDB.ts';

const {
  Terrain,
  ObliquePhotography,
  UrlTemplateImagery,
  destroyClass,
  ViewModelBase,
  HeightLinearGradientCS,
} = map3dC;

const DataTypes = {
  TERRAIN: 'TERRAIN',
  NO_FLY_ZONE: 'NO_FLY_ZONE',
  OBSTACLE: 'OBSTACLE',
  IMAGING: 'IMAGING',
  MODEL: 'MODEL',
  ANNOTATION: 'ANNOTATION',
  LAND_USE: 'LAND_USE',
};

const FileTypes = {
  TIF: 0,
  KML: 0,
  SLICE: 1,
};

class DataManagerViewModel extends ViewModelBase {
  /**
   * 构造函数
   * @param {Cesium.Viewer} viewer
   */
  constructor(viewer) {
    super();
    if (!Cesium.defined(viewer)) {
      throw new Error('viewer是必选参数。');
    }

    /**
     * @type {Cesium.Viewer}
     */
    this._viewer = viewer;
    /**
     * @type {Cesium.AssociativeArray}
     */
    this._features = new Cesium.AssociativeArray();
    /**
     * @type {Cesium.AssociativeArray}
     */
    this._cacheFeatures = new Cesium.AssociativeArray();
    /**
     * @type {string[]}
     */
    this._curFeatureIds = [];
    /**
     * URL标识符与名称的映射 {标识符: 名称}
     * @type {Map<string, string>}
     */
    this._urlIdNameMap = new Map();
  }

  /**
   * 加载数据
   * @param {object|object[]} featureJson
   * @param {string} featureJson.id 唯一标识
   * @param {string} featureJson.name 名称
   * @param {string} featureJson.dataType 数据类型，TERRAIN，NO_FLY_ZONE，OBSTACLE
   * @param {number} featureJson.fileType 文件类型，TIF-0，KML-0，SLICE-1
   * @param {string} featureJson.url 链接
   */
  async loadFeatures(featureJson) {
    if (!Array.isArray(featureJson)) {
      featureJson = [featureJson];
    }

    const featureJsonIds = featureJson.map((item) => item.id);

    const featureJsonRemove = [];
    for (let i = 0; i < this._curFeatureIds.length; i++) {
      const featureId = this._curFeatureIds[i];
      if (!featureJsonIds.includes(featureId)) {
        featureJsonRemove.push(featureId);
      }
    }
    this._removeFeature(featureJsonRemove);

    const featureJsonAdd = [];
    for (let i = 0; i < featureJson.length; i++) {
      const featureId = featureJson[i].id;
      if (!this._curFeatureIds.includes(featureId)) {
        featureJsonAdd.push(featureJson[i]);
      }
    }

    this._curFeatureIds = featureJsonIds;

    await this._addFeatures(featureJsonAdd);
  }

  /**
   * 定位数据
   * @param {object|object[]} featureJson
   * @param {string} featureJson.id 唯一标识
   * @param {string} featureJson.name 名称
   * @param {string} featureJson.dataType 数据类型，TERRAIN，NO_FLY_ZONE，OBSTACLE
   * @param {number} featureJson.fileType 文件类型，TIF-0，KML-0，SLICE-1
   * @param {string} featureJson.url 链接
   */
  async flyToFeature(featureJson) {
    const { id } = featureJson;
    const feature = this._features.get(id);
    if (feature) {
      feature.flyTo();
    }
  }

  /**
   *
   * @param {object|object[]} featureJson
   * @param {string} featureJson.id 唯一标识
   * @param {string} featureJson.name 名称
   * @param {string} featureJson.dataType 数据类型，TERRAIN，NO_FLY_ZONE，OBSTACLE
   * @param {number} featureJson.fileType 文件类型，TIF-0，KML-0，SLICE-1
   * @param {string} featureJson.url 链接
   */
  async _addFeatures(featureJson) {
    for (let i = 0; i < featureJson.length; i++) {
      const featureJsonItem = featureJson[i];
      await this._addFeature(featureJsonItem);
    }
  }

  /**
   *
   * @param {object} featureJson
   * @param {string} featureJson.id 唯一标识
   * @param {string} featureJson.name 名称
   * @param {string} featureJson.dataType 数据类型，TERRAIN，NO_FLY_ZONE，OBSTACLE
   * @param {number} featureJson.fileType 文件类型，TIF-0，KML-0，SLICE-1
   * @param {string} featureJson.url 链接
   */
  async _addFeature(featureJson) {
    const { id, dataType } = featureJson;

    try {
      let feature;
      if (this._cacheFeatures.contains(id)) {
        feature = this._cacheFeatures.get(id);
      } else {
        const createFeatureFun = this[`_create${dataType}`];
        if (createFeatureFun) {
          feature = await createFeatureFun.call(this, featureJson);
        }
        if (feature) {
          this._cacheFeatures.set(id, feature);
        }
      }

      // 解决异步导致存在未勾选的显示出来的问题
      if (this._curFeatureIds.includes(id)) {
        if (feature) {
          this._features.set(id, feature);
          feature.addToViewer(this._viewer);
        }
      }
    } catch (error) {
      console.error('加载失败！', featureJson);
      console.error(error);
    }
  }

  /**
   * 删除要素
   * @param {string[]} featureIds
   */
  _removeFeature(featureIds) {
    for (let i = 0; i < featureIds.length; i++) {
      const featureId = featureIds[i];
      if (this._features.contains(featureId)) {
        const feature = this._features.get(featureId);
        feature.removeFromViewer();
        this._features.remove(featureId);
      }
    }
  }

  /**
   * 创建地形
   * @param {object} featureJson
   * @param {string} featureJson.id 唯一标识
   * @param {string} featureJson.name 名称
   * @param {number} featureJson.fileType 文件类型，TIF-0，KML-0，SLICE-1
   * @param {string} featureJson.url 链接
   */
  async _createTERRAIN(featureJson) {
    const { id, name, dataType, fileType, url } = featureJson;
    let feature;
    if (dataType === DataTypes.TERRAIN) {
      if (fileType === FileTypes.SLICE) {
        // 地形切片
        feature = await Terrain.fromUrl(url, { id, name });
      }
    }
    return feature;
  }

  /**
   * 创建禁飞区
   * @param {object} featureJson
   * @param {string} featureJson.id 唯一标识
   * @param {string} featureJson.name 名称
   * @param {number} featureJson.fileType 文件类型，TIF-0，KML-0，SLICE-1
   * @param {string} featureJson.url 链接
   */
  async _createNO_FLY_ZONE(featureJson) {
    const { id, name, dataType, fileType, url } = featureJson;
    let feature;
    if (dataType === DataTypes.NO_FLY_ZONE) {
      if (fileType === FileTypes.KML) {
        // 禁飞区kml
        feature = await NoFlyZone.fromKml(url, {
          id,
          name,
          polygonStyle: {
            isExtrudedHeight: true,
          },
        });
      }
    }
    return feature;
  }

  /**
   * 创建障碍物
   * @param {object} featureJson
   * @param {string} featureJson.id 唯一标识
   * @param {string} featureJson.name 名称
   * @param {number} featureJson.fileType 文件类型，TIF-0，KML-0，SLICE-1
   * @param {string} featureJson.url 链接
   */
  async _createOBSTACLE(featureJson) {
    const { id, name, dataType, fileType, url } = featureJson;
    let feature;
    if (dataType === DataTypes.OBSTACLE) {
      if (fileType === FileTypes.SLICE) {
        // 3dtiles切片
        let newUrl = url;
        if (!newUrl.endsWith('tileset.json')) {
          newUrl += '/tileset.json';
          newUrl = newUrl.replace(/([^:])(\/\/+)/g, '$1/'); // 去掉多余斜杠
        }
        console.log(newUrl, name);
        // 提取URL中的标识符(最后一个路径段,tileset.json前面的部分)
        const match = newUrl.match(/(\/[^/]+\/)tileset.json/);
        if (match && match[1]) {
          const urlId = match[1]; // 保留两边的斜杠,例如 /4e5797ba7a6fe8d1575ee8f0042fe229/
          const cleanName = name.replace(/\.zip$/i, ''); // 去掉.zip后缀
          this._urlIdNameMap.set(urlId, cleanName);
          console.log('存储映射:', urlId, '→', cleanName);
          init3DTilesCache([urlId], { name: cleanName });
        }
        feature = await ObliquePhotography.fromUrl(newUrl, { id, name });

        if (name.includes('白模')) {
          const tileset = feature.tileset;
          if (name.includes('重建化简')) {
            tileset.maximumScreenSpaceError = 8;
          } else {
            tileset.maximumScreenSpaceError = 3;
          }
          tileset.dynamicScreenSpaceError = false;

          const gradientShader = HeightLinearGradientCS.from3DTileset(tileset, {
            topColor: '#0cb7ff', //顶部颜色
            mediumColor: '#0034e1', //中间颜色
            bottomColor: '#0000a7', //底部颜色
            gradientHeight: 70, //底部到中间的渐变高度
            higherHeight: 400, //中间到顶部的渐变高度
          });
          feature.applyCustomShader(gradientShader);
        }
      } else if (fileType === FileTypes.KML) {
        // 障碍物kml
        feature = await Obstacle.fromKml(url, { id, name });
      }
    }
    return feature;
  }

  /**
   * 创建影像
   * @param {object} featureJson
   * @param {string} featureJson.id 唯一标识
   * @param {string} featureJson.name 名称
   * @param {number} featureJson.fileType 文件类型，TIF-0，KML-0，SLICE-1
   * @param {string} featureJson.url 链接
   */
  async _createIMAGING(featureJson) {
    const { id, name, dataType, fileType, url } = featureJson;
    let feature;
    if (dataType === DataTypes.IMAGING) {
      if (fileType === FileTypes.SLICE) {
        let newUrl = url;
        if (!newUrl.endsWith('{z}/{x}/{y}.png')) {
          newUrl += '/{z}/{x}/{y}.png';
          newUrl = newUrl.replace(/([^:])(\/\/+)/g, '$1/'); // 去掉多余斜杠
        }
        feature = await UrlTemplateImagery.fromUrl(newUrl, { id, name });
      }
    }
    return feature;
  }

  /**
   * 创建模型
   * @param {object} featureJson
   * @param {string} featureJson.id 唯一标识
   * @param {string} featureJson.name 名称
   * @param {number} featureJson.fileType 文件类型，TIF-0，KML-0，SLICE-1
   * @param {string} featureJson.url 链接
   */
  async _createMODEL(featureJson) {
    const { id, name, dataType, fileType, url } = featureJson;
    let feature;
    if (dataType === DataTypes.MODEL) {
      if (fileType === FileTypes.SLICE) {
        // 3dtiles切片
        let newUrl = url;
        if (!newUrl.endsWith('tileset.json')) {
          newUrl += '/tileset.json';
          newUrl = newUrl.replace(/([^:])(\/\/+)/g, '$1/'); // 去掉多余斜杠
        }
        feature = await ObliquePhotography.fromUrl(newUrl, { id, name });
      }
    }

    return feature;
  }

  /**
   * 创建标注
   * @param {object} featureJson
   * @param {string} featureJson.id 唯一标识
   * @param {string} featureJson.name 名称
   * @param {number} featureJson.fileType 文件类型，TIF-0，KML-0，SLICE-1
   * @param {string} featureJson.url 链接
   */
  async _createANNOTATION(featureJson) {
    const { id, name, dataType, fileType, url } = featureJson;
    let feature;
    if (dataType === DataTypes.ANNOTATION) {
      if (fileType === FileTypes.KML) {
        if (name.includes('珠江河道')) {
          // 标注kml
          feature = await Annotation.fromKml(url, {
            id,
            name,
            polygonStyle: {
              useFlowWaterMaterial: true,
            },
          });
        } else {
          // 标注kml
          feature = await Annotation.fromKml(url, { id, name });
        }
      }
    }
    return feature;
  }

  /**
   * 创建土地利用
   * @param {object} featureJson
   * @param {string} featureJson.id 唯一标识
   * @param {string} featureJson.name 名称
   * @param {number} featureJson.fileType 文件类型，TIF-0，KML-0，SLICE-1
   * @param {string} featureJson.url 链接
   */
  async _createLAND_USE(featureJson) {
    const { id, name, dataType, fileType, url } = featureJson;
    let feature;
    if (dataType === DataTypes.LAND_USE) {
      if (fileType === FileTypes.KML) {
        // 土地利用kml
        feature = await LandUse.fromKml(url, { id, name });
      }
    }
    return feature;
  }

  /**
   * 销毁
   */
  destroy() {
    const features = this._cacheFeatures.values;
    for (let i = 0; i < features.length; i++) {
      const feature = features[i];
      feature.destroy();
    }
    this._features.removeAll();
    this._cacheFeatures.removeAll();
    this._curFeatureIds.length = 0;
    this._urlIdNameMap.clear();

    destroyClass(this);
  }
}

export default DataManagerViewModel;
